import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Plus,
  ChevronRight,
  FileText,
  CheckCircle2,
  Clock,
  ArrowRight,
  MoreVertical,
  Trash2,
  Edit3,
  Send,
  Lock,
  AlertCircle,
  RefreshCw,
  X
} from 'lucide-react';
import { api, MonthlyPlan, MonthlyPlanDetail } from '../../lib/api';
import { ClosePlanModal } from './ClosePlanModal';

interface MonthlyPlansViewProps {
  projectId: string;
  onSelectPlan?: (plan: MonthlyPlan) => void;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const getStatusColor = (status: string) => {
  switch (status) {
    case 'draft': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'published': return 'bg-green-100 text-green-800 border-green-200';
    case 'closed': return 'bg-gray-100 text-gray-600 border-gray-200';
    default: return 'bg-gray-100 text-gray-600';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'draft': return <Edit3 className="w-3.5 h-3.5" />;
    case 'published': return <CheckCircle2 className="w-3.5 h-3.5" />;
    case 'closed': return <Lock className="w-3.5 h-3.5" />;
    default: return null;
  }
};

export const MonthlyPlansView: React.FC<MonthlyPlansViewProps> = ({ projectId, onSelectPlan }) => {
  const [plans, setPlans] = useState<MonthlyPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<MonthlyPlanDetail | null>(null);
  const [actionMenuPlanId, setActionMenuPlanId] = useState<number | null>(null);

  // Create modal state
  const [newPlanYear, setNewPlanYear] = useState(new Date().getFullYear());
  const [newPlanMonth, setNewPlanMonth] = useState(new Date().getMonth() + 1);
  const [newPlanName, setNewPlanName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const data = await api.getMonthlyPlans(projectId);
      setPlans(data);
      setError(null);
    } catch (err) {
      setError('Failed to load monthly plans');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, [projectId]);

  const handleCreatePlan = async () => {
    try {
      setCreating(true);
      await api.createMonthlyPlan(projectId, {
        year: newPlanYear,
        month: newPlanMonth,
        name: newPlanName || undefined
      });
      setShowCreateModal(false);
      setNewPlanName('');
      fetchPlans();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handlePublishPlan = async (planId: number) => {
    try {
      await api.publishMonthlyPlan(planId);
      fetchPlans();
      setActionMenuPlanId(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const [closingPlan, setClosingPlan] = useState<MonthlyPlan | null>(null);

  const handleClosePlan = (plan: MonthlyPlan) => {
    setClosingPlan(plan);
    setActionMenuPlanId(null);
  };

  const handleDeletePlan = async (planId: number) => {
    if (!confirm('Are you sure you want to delete this draft plan?')) {
      return;
    }
    try {
      await api.deleteMonthlyPlan(planId);
      fetchPlans();
      setActionMenuPlanId(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleViewPlan = async (plan: MonthlyPlan) => {
    try {
      const details = await api.getMonthlyPlan(plan.id);
      setSelectedPlan(details);
      if (onSelectPlan) onSelectPlan(plan);
    } catch (err) {
      console.error(err);
    }
  };

  // Group plans by year
  const plansByYear = plans.reduce((acc, plan) => {
    if (!acc[plan.year]) acc[plan.year] = [];
    acc[plan.year].push(plan);
    return acc;
  }, {} as Record<number, MonthlyPlan[]>);

  const years = Object.keys(plansByYear).map(Number).sort((a, b) => b - a);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
        <span className="ml-2 text-gray-600">Loading plans...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Monthly Plans
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Create and manage monthly construction plans
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Monthly Plan
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Plans Grid */}
      {plans.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No Monthly Plans</h3>
          <p className="text-gray-500 mb-4">Create your first monthly plan to start organizing tasks</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Create Plan
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {years.map(year => (
            <div key={year}>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <span className="bg-gray-100 px-3 py-1 rounded-lg">{year}</span>
                <span className="text-sm font-normal text-gray-500">
                  {plansByYear[year].length} plan{plansByYear[year].length !== 1 ? 's' : ''}
                </span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {plansByYear[year]
                  .sort((a, b) => b.month - a.month)
                  .map(plan => (
                    <div
                      key={plan.id}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                    >
                      {/* Plan Header */}
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              {MONTHS[plan.month - 1]} {plan.year}
                            </h4>
                            {plan.name && plan.name !== `${MONTHS[plan.month - 1]} ${plan.year}` && (
                              <p className="text-xs text-gray-500 mt-0.5">{plan.name}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(plan.status)}`}>
                              {getStatusIcon(plan.status)}
                              {plan.status}
                            </span>
                            <div className="relative">
                              <button
                                onClick={() => setActionMenuPlanId(actionMenuPlanId === plan.id ? null : plan.id)}
                                className="p-1 hover:bg-gray-100 rounded"
                              >
                                <MoreVertical className="w-4 h-4 text-gray-400" />
                              </button>
                              {actionMenuPlanId === plan.id && (
                                <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-[140px]">
                                  {plan.status === 'draft' && (
                                    <>
                                      <button
                                        onClick={() => handlePublishPlan(plan.id)}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <Send className="w-4 h-4 text-green-600" />
                                        Publish
                                      </button>
                                      <button
                                        onClick={() => handleDeletePlan(plan.id)}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                      </button>
                                    </>
                                  )}
                                  {plan.status === 'published' && (
                                    <button
                                      onClick={() => handleClosePlan(plan)}
                                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                    >
                                      <Lock className="w-4 h-4 text-gray-600" />
                                      Close Plan
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Plan Stats */}
                      <div className="p-4 bg-gray-50">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">
                              <span className="font-medium text-gray-900">{plan.total_tasks || 0}</span> tasks
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span className="text-gray-600">
                              <span className="font-medium text-green-600">{plan.completed_tasks || 0}</span> done
                            </span>
                          </div>
                          {(plan.from_rollover || 0) > 0 && (
                            <div className="flex items-center gap-2 col-span-2">
                              <RefreshCw className="w-4 h-4 text-orange-500" />
                              <span className="text-gray-600">
                                <span className="font-medium text-orange-600">{plan.from_rollover}</span> from rollover
                              </span>
                            </div>
                          )}
                          {plan.status === 'closed' && (plan.rolled_over_tasks || 0) > 0 && (
                            <div className="flex items-center gap-2 col-span-2">
                              <ArrowRight className="w-4 h-4 text-blue-500" />
                              <span className="text-gray-600">
                                <span className="font-medium text-blue-600">{plan.rolled_over_tasks}</span> rolled over
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Plan Actions */}
                      <div className="p-3 border-t border-gray-100">
                        <button
                          onClick={() => handleViewPlan(plan)}
                          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          View Plan
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create Monthly Plan</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select
                    value={newPlanYear}
                    onChange={(e) => setNewPlanYear(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() + i - 1;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={newPlanMonth}
                    onChange={(e) => setNewPlanMonth(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {MONTHS.map((month, idx) => (
                      <option key={idx} value={idx + 1}>{month}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newPlanName}
                  onChange={(e) => setNewPlanName(e.target.value)}
                  placeholder={`${MONTHS[newPlanMonth - 1]} ${newPlanYear}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePlan}
                disabled={creating}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {creating && <RefreshCw className="w-4 h-4 animate-spin" />}
                Create Plan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Detail Modal */}
      {selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  {selectedPlan.name || `${MONTHS[selectedPlan.month - 1]} ${selectedPlan.year}`}
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(selectedPlan.status)}`}>
                    {getStatusIcon(selectedPlan.status)}
                    {selectedPlan.status}
                  </span>
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedPlan.stats.total} tasks • {selectedPlan.stats.completed} completed • {selectedPlan.stats.active} active
                </p>
              </div>
              <button
                onClick={() => setSelectedPlan(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Stats Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{selectedPlan.stats.total}</div>
                  <div className="text-sm text-blue-700">Total Tasks</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{selectedPlan.stats.completed}</div>
                  <div className="text-sm text-green-700">Completed</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-600">{selectedPlan.stats.active}</div>
                  <div className="text-sm text-yellow-700">Active</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{selectedPlan.stats.from_rollover}</div>
                  <div className="text-sm text-orange-700">From Rollover</div>
                </div>
              </div>

              {/* Tasks Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">WBS</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Task</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Location</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Source</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedPlan.tasks.map((task) => (
                      <tr key={task.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">{task.wbs_code}</td>
                        <td className="px-4 py-3 text-gray-900">{task.task_name}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {[task.building_name, task.category_name, task.floor_name].filter(Boolean).join(' → ')}
                        </td>
                        <td className="px-4 py-3">
                          {task.source === 'rollover' ? (
                            <span className="inline-flex items-center gap-1 text-orange-600">
                              <RefreshCw className="w-3.5 h-3.5" />
                              {task.source_year && `${MONTHS[task.source_month! - 1].slice(0, 3)} ${task.source_year}`}
                            </span>
                          ) : (
                            <span className="text-gray-500">Master</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                            task.status === 'completed' ? 'bg-green-100 text-green-700' :
                            task.status === 'rolled_over' ? 'bg-orange-100 text-orange-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {task.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions */}
            {selectedPlan.status === 'draft' && (
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Tasks
                </button>
                <button
                  onClick={() => {
                    handlePublishPlan(selectedPlan.id);
                    setSelectedPlan(null);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Publish Plan
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Close Plan Modal */}
      {closingPlan && (
        <ClosePlanModal
          isOpen={true}
          onClose={() => setClosingPlan(null)}
          plan={closingPlan}
          availablePlans={plans}
          onSuccess={() => {
            fetchPlans();
            setClosingPlan(null);
          }}
        />
      )}
    </div>
  );
};

export default MonthlyPlansView;


